import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { epcProjectService } from '../../../../services/epcProjectService';
import EpcMppImportsPanel from '../../mpp/EpcMppImportsPanel';
import { useEpcProjectDetail } from '../EpcProjectDetailContext';

/**
 * Microsoft Project (.mpp) integration: file upload, URL import, and history.
 * Kept separate from the schedule workspace so imports stay in one place.
 */
export default function EpcProjectMppTab() {
  const { id, canImport, importHistory, reload } = useEpcProjectDetail();
  const navigate = useNavigate();
  const [urlInput, setUrlInput] = useState('');
  const [mppBusy, setMppBusy] = useState(false);

  const onUrlImport = async () => {
    if (!id || !urlInput.trim()) return;
    try {
      setMppBusy(true);
      const result = await epcProjectService.importMppUrl(id, urlInput.trim());
      setUrlInput('');
      if (result.staged) {
        const sid = result.import?.id ?? result.import?._id;
        toast('Schedule staged — review changes before applying');
        navigate(`/epc/projects/${id}/schedule/compare/${sid}`);
        return;
      }
      toast.success('Imported from URL');
      await reload();
    } catch (err) {
      console.error(err);
      toast.error('URL import failed (check allowlist / converter)');
    } finally {
      setMppBusy(false);
    }
  };

  return (
    <div className="space-y-2">
      <div className="mb-6 max-w-3xl">
        <h2 className="text-lg font-semibold text-slate-900">Microsoft Project integration</h2>
        <p className="mt-1 text-sm text-slate-600 leading-relaxed">
          Upload <code className="rounded bg-slate-100 px-1 text-xs">.mpp</code> or MS Project XML{' '}
          <code className="rounded bg-slate-100 px-1 text-xs">.xml</code> files, import from an allowlisted URL,
          and review import history. After a successful import, open the{' '}
          <span className="font-medium text-slate-800">Schedule</span> tab to browse and edit tasks.
        </p>
      </div>
      <EpcMppImportsPanel
        projectId={id}
        canImport={canImport}
        urlInput={urlInput}
        setUrlInput={setUrlInput}
        mppBusy={mppBusy}
        onUrlImport={() => void onUrlImport()}
        importHistory={importHistory}
        onImported={reload}
      />
    </div>
  );
}
